/**
 * RouteTracker — emits a `page_view` event on every route change.
 *
 * - Uses React Router's useLocation so it works with both `pushState` and
 *   back/forward navigation.
 * - Tracks `duration` as the time spent on the PREVIOUS page, written when
 *   the next page_view is emitted. The very first page_view has duration=null.
 * - Guards against React StrictMode double-invocation (effect runs twice in
 *   dev) with a ref keyed on pathname+search.
 * - Also updates the shared client's current page so that subsequent events
 *   emitted from the page (button clicks, API errors, etc.) carry the
 *   correct `page` context.
 *
 * Mount INSIDE <BrowserRouter> and inside <UserLogProvider>.
 */

import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { userLogClient } from "./userLogClient";
import { LogAction } from "./types";

export const RouteTracker: React.FC = () => {
	const location = useLocation();
	const prevKeyRef = useRef<string | null>(null);
	const pageEnteredAtRef = useRef<number>(performance.now());

	useEffect(() => {
		const key = `${location.pathname}${location.search}`;

		// Skip duplicate fire caused by React 18 StrictMode remount in dev.
		if (prevKeyRef.current === key) return;

		const now = performance.now();
		const duration = prevKeyRef.current === null ? null : Math.round(now - pageEnteredAtRef.current);

		userLogClient.setCurrentPage(location.pathname);
		userLogClient.logEvent({
			action: LogAction.PAGE_VIEW,
			duration,
			properties: {
				path: location.pathname,
				search: location.search || null,
				// Route template resolution is a nice-to-have — we'd need to
				// thread the matched <Route> here. Leaving `route` out for now
				// so we don't over-fit to react-router internals.
				referrer: prevKeyRef.current,
			},
		});

		prevKeyRef.current = key;
		pageEnteredAtRef.current = now;
	}, [location.pathname, location.search]);

	return null;
};
